import React, { useEffect, useState } from 'react';
import { departmentApi } from '../../../api/adminapi';
import { toast } from 'react-toastify';
import { MdAdd, MdEdit, MdDelete, MdClose, MdSearch, MdBusiness, MdRefresh } from 'react-icons/md';
import './DepartmentManagement.css';

function DepartmentManagement() {
  const [departments, setDepartments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingDepartment, setDeletingDepartment] = useState(null);

  const [formData, setFormData] = useState({
    departmentCode: '',
    departmentName: '',
    description: '',
    displayOrder: ''
  });

  useEffect(() => {
    fetchDepartments();
  }, []);

  const fetchDepartments = async () => {
    try {
      setIsLoading(true);
      setError('');

      const response = await departmentApi.getAll();
      const data = response?.data || response || [];

      setDepartments(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching departments:', err);
      setError(err.response?.data?.message || err.message || 'Failed to fetch departments');
      toast.error('Failed to load departments');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddClick = () => {
    setIsEditMode(false);
    setEditingDepartment(null);
    setFormData({
      departmentCode: '',
      departmentName: '',
      description: '',
      displayOrder: ''
    });
    setShowModal(true);
  };

  const handleEditClick = (dept) => {
    setIsEditMode(true);
    setEditingDepartment(dept);
    setFormData({
      departmentCode: dept.departmentCode || '',
      departmentName: dept.departmentName || '',
      description: dept.description || '',
      displayOrder: dept.displayOrder || ''
    });
    setShowModal(true);
  };

  const handleDeleteClick = (dept) => {
    setDeletingDepartment(dept);
    setShowDeleteModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingDepartment(null);
    setIsEditMode(false);
    setFormData({
      departmentCode: '',
      departmentName: '',
      description: '',
      displayOrder: ''
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.departmentCode?.trim()) {
      toast.warn('Department code is required');
      return;
    }
    if (!formData.departmentName?.trim()) {
      toast.warn('Department name is required');
      return;
    }

    try {
      setIsLoading(true);

      const payload = {
        departmentCode: formData.departmentCode.trim().toUpperCase(),
        departmentName: formData.departmentName.trim(),
        description: formData.description?.trim() || null,
        displayOrder: formData.displayOrder ? parseInt(formData.displayOrder, 10) : null
      };

      if (isEditMode && editingDepartment) {
        await departmentApi.update(editingDepartment.id, payload);
        toast.success('Department updated successfully');
      } else {
        await departmentApi.create(payload);
        toast.success('Department created successfully');
      }

      handleCloseModal();
      await fetchDepartments();
    } catch (err) {
      console.error('Error saving department:', err);
      const errorMsg = err.response?.data?.message || err.message || 'Failed to save department';
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingDepartment) return;

    try {
      setIsLoading(true);
      await departmentApi.delete(deletingDepartment.id);
      toast.success('Department deleted successfully');
      setShowDeleteModal(false);
      setDeletingDepartment(null);
      await fetchDepartments();
    } catch (err) {
      console.error('Error deleting department:', err);
      const errorMsg = err.response?.data?.message || err.message || 'Failed to delete department';
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusToggle = async (dept) => {
    try {
      const newStatus = dept.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
      await departmentApi.updateStatus(dept.id, newStatus);
      toast.success(`Department ${newStatus === 'ACTIVE' ? 'activated' : 'deactivated'} successfully`);
      await fetchDepartments();
    } catch (err) {
      console.error('Error updating status:', err);
      toast.error('Failed to update department status');
    }
  };

  const handleInitialize = async () => {
    try {
      setIsLoading(true);
      await departmentApi.initialize();
      toast.success('Default departments initialized');
      await fetchDepartments();
    } catch (err) {
      console.error('Error initializing departments:', err);
      toast.error('Failed to initialize departments');
    } finally {
      setIsLoading(false);
    }
  };

  const filteredDepartments = departments.filter(dept => {
    const searchLower = searchTerm.toLowerCase();
    return (
      dept.departmentCode?.toLowerCase().includes(searchLower) ||
      dept.departmentName?.toLowerCase().includes(searchLower) ||
      dept.description?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className="admin-content department-management">
      {/* Header */}
      <div className="page-header-compact">
        <div className="header-left-compact">
          <h2 className="page-title-compact">
            <MdBusiness className="title-icon" />
            Department Management
          </h2>
          <span className="header-subtitle">Manage organizational departments</span>
        </div>
        <div className="header-actions-compact">
          <div className="search-compact">
            <MdSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search departments..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button className="btn btn-secondary" onClick={fetchDepartments} disabled={isLoading}>
            <MdRefresh />
          </button>
          {departments.length === 0 && (
            <button className="btn btn-secondary" onClick={handleInitialize} disabled={isLoading}>
              Initialize Defaults
            </button>
          )}
          <button className="btn btn-primary add-btn" onClick={handleAddClick}>
            <MdAdd /> Add Department
          </button>
        </div>
      </div>

      {/* Error State */}
      {error && departments.length === 0 && (
        <div className="error-container">
          <p className="error-text">{error}</p>
          <button className="retry-button" onClick={fetchDepartments}>Retry</button>
        </div>
      )}

      {/* Loading State */}
      {isLoading && departments.length === 0 ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading departments...</p>
        </div>
      ) : (
        <div className="table-container">
          {filteredDepartments.length === 0 ? (
            <div className="no-data">
              <MdBusiness className="no-data-icon" />
              <p>{searchTerm ? 'No departments found matching your search' : 'No departments found. Click "Add Department" to create one.'}</p>
            </div>
          ) : (
            <table className="departments-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Name</th>
                  <th>Description</th>
                  <th>Order</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredDepartments.map(dept => (
                  <tr key={dept.id} className={dept.status === 'INACTIVE' ? 'inactive-row' : ''}>
                    <td className="dept-code">{dept.departmentCode}</td>
                    <td className="dept-name">{dept.departmentName}</td>
                    <td className="dept-description">{dept.description || '-'}</td>
                    <td className="dept-order">{dept.displayOrder || '-'}</td>
                    <td>
                      <button
                        className={`status-badge ${dept.status === 'ACTIVE' ? 'active' : 'inactive'}`}
                        onClick={() => handleStatusToggle(dept)}
                        title={`Click to ${dept.status === 'ACTIVE' ? 'deactivate' : 'activate'}`}
                      >
                        {dept.status}
                      </button>
                    </td>
                    <td className="actions-cell">
                      <button
                        className="btn-icon btn-edit"
                        onClick={() => handleEditClick(dept)}
                        title="Edit"
                      >
                        <MdEdit />
                      </button>
                      <button
                        className="btn-icon btn-delete"
                        onClick={() => handleDeleteClick(dept)}
                        title="Delete"
                      >
                        <MdDelete />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{isEditMode ? 'Edit Department' : 'Add Department'}</h2>
              <button className="btn-close" onClick={handleCloseModal}>
                <MdClose />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label htmlFor="departmentCode">Department Code *</label>
                  <input
                    type="text"
                    id="departmentCode"
                    name="departmentCode"
                    value={formData.departmentCode}
                    onChange={handleInputChange}
                    placeholder="e.g., PD, RCM, IT"
                    required
                    disabled={isEditMode}
                  />
                  {isEditMode && (
                    <small className="help-text">Code cannot be changed after creation</small>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="departmentName">Department Name *</label>
                  <input
                    type="text"
                    id="departmentName"
                    name="departmentName"
                    value={formData.departmentName}
                    onChange={handleInputChange}
                    placeholder="e.g., Product Development"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="description">Description</label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Optional description"
                    rows={3}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="displayOrder">Display Order</label>
                  <input
                    type="number"
                    id="displayOrder"
                    name="displayOrder"
                    value={formData.displayOrder}
                    onChange={handleInputChange}
                    placeholder="e.g., 1, 2, 3..."
                    min="1"
                  />
                  <small className="help-text">Lower numbers appear first in dropdowns</small>
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleCloseModal} disabled={isLoading}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={isLoading}>
                  {isLoading ? 'Saving...' : (isEditMode ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deletingDepartment && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-content delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Delete Department</h2>
              <button className="btn-close" onClick={() => setShowDeleteModal(false)}>
                <MdClose />
              </button>
            </div>

            <div className="modal-body">
              <p>Are you sure you want to delete the department <strong>{deletingDepartment.departmentName}</strong> ({deletingDepartment.departmentCode})?</p>
              <p className="warning-text">This action will deactivate the department. Users currently assigned to this department will not be affected.</p>
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowDeleteModal(false)} disabled={isLoading}>
                Cancel
              </button>
              <button className="btn btn-danger" onClick={handleDelete} disabled={isLoading}>
                {isLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DepartmentManagement;
